/**
 * Error codes mapping - should match backend error codes
 */
import { API_BASE_URL } from '../config/api';

export const ErrorCodes = {
    // Authentication errors
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    INVALID_TOKEN: 'INVALID_TOKEN',
    UNAUTHORIZED: 'UNAUTHORIZED',
    FORBIDDEN: 'FORBIDDEN',

    // Validation errors
    VALIDATION_ERROR: 'VALIDATION_ERROR',
    INVALID_INPUT: 'INVALID_INPUT',
    RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
    RESOURCE_EXISTS: 'RESOURCE_EXISTS',

    // Business logic errors
    INSUFFICIENT_INVENTORY: 'INSUFFICIENT_INVENTORY',
    PAYMENT_FAILED: 'PAYMENT_FAILED',
    ORDER_PROCESSING_ERROR: 'ORDER_PROCESSING_ERROR',

    // Server errors
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    DATABASE_ERROR: 'DATABASE_ERROR',
    EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',

    // Network errors
    NETWORK_ERROR: 'NETWORK_ERROR',
    TIMEOUT_ERROR: 'TIMEOUT_ERROR',

    // Rate limiting
    RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED'
};

/**
 * Get a user-friendly error message based on error code
 * @param {string} errorCode - The error code
 * @param {string} defaultMessage - Default message if no specific message is found
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyErrorMessage = (errorCode, defaultMessage = 'Something went wrong. Please try again.') => {
    const errorMessages = {
        [ErrorCodes.INVALID_CREDENTIALS]: 'The email or password you entered is incorrect.',
        [ErrorCodes.TOKEN_EXPIRED]: 'Your session has expired. Please log in again.',
        [ErrorCodes.INVALID_TOKEN]: 'Authentication failed. Please log in again.',
        [ErrorCodes.UNAUTHORIZED]: 'You need to be logged in to access this feature.',
        [ErrorCodes.FORBIDDEN]: 'You don\'t have permission to access this resource.',
        [ErrorCodes.VALIDATION_ERROR]: 'Please check your input and try again.',
        [ErrorCodes.INVALID_INPUT]: 'The information you provided is invalid.',
        [ErrorCodes.RESOURCE_NOT_FOUND]: 'The requested resource could not be found.',
        [ErrorCodes.RESOURCE_EXISTS]: 'This resource already exists.',
        [ErrorCodes.INSUFFICIENT_INVENTORY]: 'This item is currently out of stock or has insufficient quantity.',
        [ErrorCodes.PAYMENT_FAILED]: 'Your payment could not be processed. Please try again or use a different payment method.',
        [ErrorCodes.ORDER_PROCESSING_ERROR]: 'There was an issue processing your order. Please try again.',
        [ErrorCodes.INTERNAL_ERROR]: 'An internal error occurred. Our team has been notified.',
        [ErrorCodes.DATABASE_ERROR]: 'A database error occurred. Please try again later.',
        [ErrorCodes.EXTERNAL_SERVICE_ERROR]: 'A service we depend on is currently unavailable. Please try again later.',
        [ErrorCodes.NETWORK_ERROR]: 'Network connection issue. Please check your internet connection and try again.',
        [ErrorCodes.TIMEOUT_ERROR]: 'The request timed out. Please try again.',
        [ErrorCodes.RATE_LIMIT_EXCEEDED]: 'Too many requests. Please try again later.'
    };

    return errorMessages[errorCode] || defaultMessage;
};

/**
 * Format validation errors from the backend into a user-friendly format
 * @param {Array|Object} errors - Validation errors from the backend
 * @returns {Object} Formatted errors object with field names as keys
 */
export const formatValidationErrors = (errors) => {
    if (!errors) return {};

    // If errors is an array of objects with field and message properties
    if (Array.isArray(errors)) {
        return errors.reduce((acc, error) => {
            if (error.field) {
                acc[error.field] = error.message;
            }
            return acc;
        }, {});
    }

    // If errors is already an object with field names as keys
    if (typeof errors === 'object') {
        return errors;
    }

    return {};
};

/**
 * Log error to backend error logging service
 * @param {Error} error - The error object
 * @param {Object} context - Additional context information
 */
export const logErrorToService = async (error, context = {}) => {
    try {
        const errorData = {
            message: error?.message || error?.toString() || 'Unknown error',
            errorCode: error?.errorCode || context?.errorCode || 'FRONTEND_ERROR',
            statusCode: error?.statusCode || context?.statusCode || 0,
            stack: error?.stack || (error instanceof Error ? error.stack : null),
            componentName: context?.componentName || 'Unknown',
            url: context?.url || window.location.href,
            userAgent: navigator.userAgent,
            details: error?.details || context?.details || null,
            context: {
                ...context,
                timestamp: new Date().toISOString()
            },
            requestId: context?.requestId || null
        };

        // Send to backend error logging endpoint (use production HTTPS default)
        const base = (API_BASE_URL || 'https://api.knotsandkrafts.in/api').replace(/\/+$/, '');
        // Use fetch with keepalive to ensure request is sent even if page is unloading
        fetch(`${base}/errors/log`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(errorData),
            keepalive: true // Ensures request is sent even if page is unloading
        }).catch(err => {
            // Silently fail if error logging fails to avoid infinite loops
            console.warn('Failed to log error to backend:', err);
        });

        // Also log to console in development
        if (process.env.NODE_ENV !== 'production') {
            console.error('Error logged to service:', error, context);
        }
    } catch (loggingError) {
        // Silently fail to avoid infinite error loops
        console.warn('Error logging failed:', loggingError);
    }
};

/**
 * Handle API error responses
 * @param {Error} error - The error object from axios or fetch
 * @returns {Object} Normalized error object
 */
export const handleApiError = (error) => {
    let errorData = {
        message: 'An unexpected error occurred',
        statusCode: 500,
        errorCode: ErrorCodes.INTERNAL_ERROR,
        details: null,
        requestId: null
    };

    // Handle axios errors
    if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const { data, status } = error.response;

        errorData = {
            message: data.message || errorData.message,
            statusCode: status,
            errorCode: data.errorCode || errorData.errorCode,
            details: data.details || null,
            requestId: data.requestId || null
        };
    } else if (error.request) {
        // The request was made but no response was received
        errorData = {
            message: 'No response received from server',
            statusCode: 0,
            errorCode: ErrorCodes.NETWORK_ERROR,
            details: null,
            requestId: null
        };
    } else {
        // Something happened in setting up the request that triggered an Error
        errorData = {
            message: error.message || errorData.message,
            statusCode: 0,
            errorCode: ErrorCodes.INTERNAL_ERROR,
            details: null,
            requestId: null
        };
    }

    // Add user-friendly message
    errorData.userMessage = getUserFriendlyErrorMessage(errorData.errorCode, errorData.message);

    return errorData;
};